/**
 * accesoPerdido.ts — un 401 de la API cierra la sesión del panel v1 (6-oct-2026).
 *
 * El v1 no sabía qué hacer con un 401: el código guardado solo se borraba al
 * cerrar sesión, así que una pestaña con un código que ya no vale (rotado, o
 * el ANTERIOR quitado del .env) seguía mandándolo para siempre: el sondeo
 * cada 10-15 s y el ticket del WebSocket cada minuto. Cada uno de esos 401
 * cuenta en el límite de fallos del servidor (20 en 15 min por IP,
 * server/middleware/limiteFallosAuth.js): en unos 3 minutos la IP de casa
 * quedaba fuera, también con el código bueno, ventana tras ventana.
 *
 * Se vigila en UN sitio, `fetch`, porque el v1 llama a la API desde muchos
 * (lib/api.ts, SwimLane, las notificaciones, Polymarket, el ticket del WS).
 * Un 401 de /api enviado con el código que hay guardado lo borra y avisa con
 * el evento ACCESO_PERDIDO; App vuelve al login y, sin código, nadie vuelve a
 * preguntar. Un 401 de una petición hecha con OTRO código (una que salió antes
 * de volver a entrar) no toca la sesión nueva. Un 429 o un 503 no son un
 * código malo: no cierran nada.
 */

import { clearAuthToken, getAuthToken } from "./api";

export const ACCESO_PERDIDO = "sorv:acceso-perdido";

export function perderAcceso(): void {
  clearAuthToken();
  window.dispatchEvent(new Event(ACCESO_PERDIDO));
}

function urlDe(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

function esDeLaApi(input: RequestInfo | URL): boolean {
  try {
    const u = new URL(urlDe(input), window.location.href);
    return u.origin === window.location.origin && u.pathname.startsWith("/api/");
  } catch {
    return false;
  }
}

const BEARER = /^Bearer\s+(.+)$/i;

/** El código que llevaba la petición: `Authorization: Bearer` o `x-access-code`. */
function codigoEnviado(input: RequestInfo | URL, init?: RequestInit): string | null {
  const cabeceras = new Headers(input instanceof Request ? input.headers : undefined);
  if (init?.headers) new Headers(init.headers).forEach((valor, nombre) => cabeceras.set(nombre, valor));
  const bearer = BEARER.exec(cabeceras.get("authorization") ?? "");
  if (bearer && bearer[1]) return bearer[1];
  return cabeceras.get("x-access-code");
}

/** Pone la vigilancia sobre window.fetch. Devuelve cómo quitarla. */
export function vigilarAcceso(): () => void {
  const original = window.fetch;
  const vigilado: typeof window.fetch = async (input, init) => {
    const res = await original.call(window, input, init);
    if (res.status === 401 && esDeLaApi(input)) {
      const guardado = getAuthToken();
      if (guardado && codigoEnviado(input, init) === guardado) perderAcceso();
    }
    return res;
  };
  window.fetch = vigilado;
  return () => {
    if (window.fetch === vigilado) window.fetch = original;
  };
}
